import { last } from 'es-toolkit';
import { SourceTypes } from '../../../../../constants';
import { buildProxyURL, handleResponseError } from '../../../utils';
import type { ProviderType } from '../../types';
import type { TwitterSourceValue } from '../types';
import type { LegacyTwitterResponseType } from './types';
import { formatTwitterResponse, formatTwitterStatus } from './utils';

const PAGE_LENGTH = 21;
const TWEET_LIMIT = 10;
const SOURCE_TYPE = SourceTypes.TWITTER;

export function createTwitterHashtagProvider(
  {
    authentication,
    source = '',
    showReplies: replies = true,
    showReposts: retweets = true,
    showPosts: tweets = true
  }: TwitterSourceValue,
  globalKey: string[],
  filtersKey: string[]
): ProviderType<LegacyTwitterResponseType> {
  const { identity = '', isFreeUser = false } = authentication ?? {};
  const normalizedTwitterHashtag = `#${source.replace(/^#/, '')}`;

  const key = [
    ...globalKey,
    ...filtersKey,
    identity,
    replies,
    retweets,
    tweets,
    normalizedTwitterHashtag
  ];

  const tweetTypes = { replies, retweets, tweets };

  const select = (response: LegacyTwitterResponseType) => {
    handleResponseError(response, SOURCE_TYPE, 'data');

    const normalizedResponse = formatTwitterResponse(response, tweetTypes);

    return normalizedResponse
      .slice(0, PAGE_LENGTH - 1)
      .filter(({ retweet }) => !retweet)
      .map((tweet) => formatTwitterStatus(tweet));
  };

  const page = (rawData: LegacyTwitterResponseType | undefined) => {
    if (rawData?.data?.length && rawData.data.length < PAGE_LENGTH) {
      return undefined;
    }

    const qSearchParams = new URLSearchParams({
      // Для получения основного массива твитов data
      query: normalizedTwitterHashtag,
      // Для получения includes: { users, media, tweets }
      expansions:
        'author_id,attachments.media_keys,referenced_tweets.id,entities.mentions.username',
      // Для получения расширенных полей твита
      'tweet.fields':
        'created_at,entities,attachments,public_metrics,in_reply_to_user_id',
      // Для получения ссылки на изображение в includes: { user }
      'user.fields': 'profile_image_url,verified',
      // Для получения ссылки на media в includes: { media }
      'media.fields': 'url,preview_image_url',
      max_results: `${TWEET_LIMIT}`
    });

    if (rawData?.data.length) {
      qSearchParams.append('until_id', last(rawData.data)!.id);
    }

    const requestURL = `tweets/search/recent?${qSearchParams}`;

    return buildProxyURL({
      apiSource: `v2/${SOURCE_TYPE}`,
      params: {
        q: requestURL,
        user_id: identity,
        // Параметр, необходимый для увеличения кеша для не платящих клиентов
        // https://elfsight.atlassian.net/browse/WIDGETS-1348
        ...(isFreeUser ? { tfp: '1' } : {})
      }
    });
  };

  return { key, page, select };
}
